import { caseConverterClient, paramsSerializerV0 } from "@/api/client"
import { type ApiError, normalizeApiError } from "@/api/errors"
import type { ParameterizedTemplateHook } from "@/api/templateHooks"
import type { TagMatchMode, TemplateTag } from "@/api/templateTags"
import type { ValidationHook } from "@/api/validationHooks"
import type { ValidationCode } from "@/gen/validation_code"
import type { VariantSetMod } from "@/util/enums"
import { Err, Ok, type Result } from "@/util/result"
import type { RequiredKeys } from "@/util/types"
import type { CurrencySymbol, DateOrder } from "@/util/validatorConstants"

export enum TemplateCreationSource {
  EXTERNAL_API = "EXTERNAL_API",
  DUPLICATION = "DUPLICATION",
  CSV_INFERENCE = "CSV_INFERENCE",
  NEW_TEMPLATE_BUTTON = "NEW_TEMPLATE_BUTTON",
  JSON_IMPORT = "JSON_IMPORT",
  TEMPLATE_AGENT = "TEMPLATE_AGENT",
}

export interface BaseTemplate {
  id: number
  label: string
  description: string
  rowConstraints: RowConstraint[]
  validationHooks: ValidationHook[]
  mappingHooks?: ParameterizedTemplateHook[]
  snapshots?: TemplateSnapshot[]
  snapshotForEnvironment?: SnapshotIdForEnvironmentId
  templateKey: string
  creationSource: TemplateCreationSource
  additionalSupportedExtensions?: string[]
  customTemplateFileName?: string
  customTemplateFileKey?: string

  isSample: boolean
  activeUsageByProduct: ActiveUsageByProduct
  archivedAt?: string | null

  /**
   * Tags attached to this template. Optional to stay backwards-compatible
   * with responses from backends where the TemplateTags feature is disabled
   * and the packer omits the field.
   */
  templateTags?: TemplateTag[]
}

export interface ActiveUsageByProduct {
  fileFeeds?: number
  workflows?: number
}

export interface DenormalizedTemplate extends BaseTemplate {
  targetAttributes: TargetAttribute[]
}

export interface Template extends BaseTemplate {
  targetAttributeIds: number[]
}

export type TemplateWithSnapshots = RequiredKeys<
  DenormalizedTemplate & Template,
  "snapshots" | "snapshotForEnvironment"
>

export interface TemplateSkeleton {
  id: number
  label: string
  description?: string
  templateKey: string
  creationSource: TemplateCreationSource

  isSample: boolean
  activeUsageByProduct: ActiveUsageByProduct
}

export interface TemplatesPageTemplate extends TemplateSkeleton {
  lastPushedEnvironmentIds: number[]
  // TODO(jenn): remove undefined when feature flag GA-ed
  unpushedChangeCount?: number
  /**
   * Tags attached to this template. Optional to stay backwards-compatible
   * with responses from backends where the TemplateTags feature is disabled
   * and the packer omits the field.
   */
  templateTags?: TemplateTag[]
  definingWorkflowId?: number | null
  definingWorkflowName?: string | null
}

export interface TemplateSnapshot {
  id: number
  createdAt: string
  creatorUserIdentifier: string
  sourceCommitUrl: string | null
}

export interface SnapshotIdForEnvironmentId {
  [environmentId: number]: number
}

export interface RowConstraint {
  id: number
  errorString: string
  sourceKey: string
  sourceIs: string[]
  targetKey: string
  targetExcludes?: string[]
  targetIs?: string[]
  isSourceExcludes: boolean
}

// TODO: Expand this.
export interface TargetAttributeOptions extends Record<string, unknown> {
  minNum?: number | null
  maxNum?: number | null
  numDecimals?: number | null
  // FILE_NAME validator
  extensions?: string[]
  // BOOLEAN validator
  trueLabel?: string
  falseLabel?: string
}

export interface DenormalizedTargetAttribute {
  id: number
  label: string
  targetAttributeKey: string
  description: string
  isCustom: boolean
  multiDelim: string
  isUnique: boolean
  isRequired: boolean
  letterCase: number
  dataType: ValidationCode
  defaultValue: string
  picklistOptions?: PicklistOption[]
  minCharLimit: number
  maxCharLimit: number
  mustExist: boolean
  index: number
  mappingHints: string[]
  isLocked: boolean
  isMultiMappable: boolean
  isHidden: boolean
  isUnmappable: boolean
  options: TargetAttributeOptions | null
}

export const PENDING_TARGET_ATTRIBUTE_ID = -1
export type PendingTargetAttribute = Partial<DenormalizedTargetAttribute> & {
  id: number // positive | typeof PENDING_TARGET_ATTRIBUTE_ID
}
export type TemplateTableTargetAttribute =
  PendingTargetAttribute | DenormalizedTargetAttribute

export interface TargetAttributeFormValues {
  label?: string
  targetAttributeKey?: string
  description?: string
  isCustom?: boolean
  multiDelim?: string
  isUnique?: boolean
  isRequired?: boolean
  letterCase?: number
  dataType?: ValidationCode
  defaultValue?: string
  picklistOptions?: string
  minCharLimit?: number
  maxCharLimit?: number
  mustExist?: boolean
  mappingHints?: string
  options?: TargetAttributeFormOptions
}

export interface TargetAttributeFormOptions {
  // Custom Regex
  regex?: string
  errorMessage?: string
  // Number
  allowThousandSeparators?: boolean
  onlyInt?: boolean
  numDecimals?: number
  minNum?: number
  maxNum?: number
  // Names
  allowInitials?: boolean
  allowSpaces?: boolean
  allowTitles?: boolean
  allowImproperCase?: boolean
  // Zip Code
  usZipCode?: boolean
  // File Name
  extensions?: string
  // Enum
  variantSetMods?: VariantSetMod[]
  // Dates
  inputDateOrder?: DateOrder
  // Money
  currencySymbol?: CurrencySymbol
}

export interface TemplateColumnOverride {
  key: string
  label?: string
  data_type?: string
  validation_options?: Record<string, unknown>
  description?: string
  is_custom?: boolean
  is_required?: boolean
  is_unique?: boolean
  letter_case?: string
  min_char_limit?: number
  max_char_limit?: number
  delimiter?: string
  must_exist?: boolean
  default_value?: string
  mapping_hints?: string[]
  is_unmappable?: boolean
}

export interface TemplateOverrides {
  columns: TemplateColumnOverride[]
  columns_to_add: TemplateColumnOverride[]
  columns_to_update: TemplateColumnOverride[]
  columns_to_remove: Record<string, unknown>
}

export interface TemplateUpdate {
  label?: string
  templateKey?: string
  description?: string
  /**
   * Full-replace set of template-tag IDs to attach to this template. Gated
   * behind the `TemplateTags` feature flag on the backend.
   */
  templateTagIds?: number[]
}

export type TargetAttribute = DenormalizedTargetAttribute

export interface PicklistOption {
  id: number
  value: string
  color: string
  description?: string
}

export async function importTemplate(file: File) {
  const formData = new FormData()
  formData.append("file", file)
  return caseConverterClient.postForm<DenormalizedTemplate>(
    "/api/templates/import",
    formData,
  )
}

export async function importTemplateFromJson(templateJson: Record<string, unknown>) {
  const blob = new Blob([JSON.stringify(templateJson)], { type: "application/json" })
  const file = new File([blob], "template.json", { type: "application/json" })
  const formData = new FormData()
  formData.append("file", file)
  return caseConverterClient.postForm<DenormalizedTemplate>(
    "/api/templates/import",
    formData,
  )
}

export async function checkTemplateKeyAvailable(templateKey: string): Promise<boolean> {
  const response = await getTemplates({
    filterModel: {
      template_key: { filterType: "key", values: [templateKey] },
    },
    pageSize: 1,
    page: 1,
  })
  return response.data.templates.length === 0
}

export async function createTemplateViaInference(file: File, sheetName?: string) {
  const formData = new FormData()
  formData.append("file", file)
  if (sheetName != null) {
    formData.append("sheetName", sheetName)
  }

  return caseConverterClient.postForm<DenormalizedTemplate>("/template-upload", formData)
}

export async function putTemplate(templateId: number, update: TemplateUpdate) {
  return caseConverterClient.put<DenormalizedTemplate>(
    `/api/templates/${templateId}`,
    update,
  )
}

export async function updateTemplateCustomTemplateFile(
  templateId: number,
  file: File | null,
) {
  let body = null
  if (file) {
    body = new FormData()
    body.append("customTemplateFile", file)
  }
  return caseConverterClient.putForm<DenormalizedTemplate>(
    `/api/templates/${templateId}/custom-template`,
    body,
  )
}

export async function postTemplate(label: string, description?: string) {
  return caseConverterClient.post<DenormalizedTemplate>("api/templates", {
    label,
    description,
  })
}

export async function bulkUpdateTargetAttributes(
  templateId: number,
  params: { isRequired?: boolean; mustExist?: boolean },
) {
  return caseConverterClient.patch<DenormalizedTemplate>(
    `/api/templates/${templateId}/bulk-target-attributes`,
    params,
  )
}

export async function duplicateTemplate(
  templateId: number,
  label: string,
  description: string,
  templateTagIds?: number[],
) {
  return caseConverterClient.post<DenormalizedTemplate>(
    `/api/templates/${templateId}/duplicate`,
    {
      label,
      description,
      templateTagIds,
    },
  )
}

export async function getExcelTemplate(templateId: number) {
  return caseConverterClient.get<Blob>(`/api/templates/${templateId}/excel-template`, {
    responseType: "blob",
  })
}

/**
 * Fetches the denormalized templates array that is normally inlined into
 * viewData under the `templates` key. Used to lazy-hydrate the Redux store
 * after first paint when the bootstrap blob is slimmed (SlimViewData). The
 * response shape is byte-identical to the viewData value, so it flows through
 * the same `initializeTemplatesFromViewData` normalization path.
 */
export async function getViewDataTemplates() {
  return caseConverterClient.get<DenormalizedTemplate[]>("/api/templates/view-data")
}

export async function deleteTemplate(templateId: number) {
  return caseConverterClient.delete<void>(`/api/templates/${templateId}`)
}

/**
 * Subset of BaseTemplate fields returned by the archived templates endpoints.
 * The backend packer only includes base fields + the :archived_at trait
 * (no :validation_hooks, :row_constraints, etc.).
 */
export type ArchivedTemplate = Pick<
  BaseTemplate,
  | "id"
  | "label"
  | "description"
  | "templateKey"
  | "isSample"
  | "creationSource"
  | "customTemplateFileName"
  | "customTemplateFileKey"
  | "activeUsageByProduct"
  | "archivedAt"
>

export async function getArchivedTemplates() {
  return caseConverterClient.get<ArchivedTemplate[]>("/api/templates/archived")
}

export async function unarchiveTemplate(
  templateId: number,
): Promise<Result<ArchivedTemplate, ApiError>> {
  try {
    const response = await caseConverterClient.post<ArchivedTemplate>(
      `/api/templates/${templateId}/unarchive`,
    )
    return Ok(response.data)
  } catch (err) {
    return Err(normalizeApiError(err, "Failed to restore template"))
  }
}

export async function patchDragTargetAttribute(
  templateId: number,
  newOrder: TargetAttribute[],
) {
  return caseConverterClient.patch<void>(
    `/api/templates/${templateId}/target-attribute-drag`,
    { newOrder },
  )
}

export async function setTemplateRowConstraints(
  templateId: number,
  rowConstraints: unknown[],
) {
  return caseConverterClient.put<DenormalizedTemplate>(
    `/api/templates/${templateId}/row-constraints`,
    {
      rowConstraints,
    },
  )
}

export async function postTargetAttribute(
  templateId: number,
  targetAttribute: Partial<TargetAttribute>,
) {
  return caseConverterClient.post<DenormalizedTargetAttribute>(
    `/api/templates/${templateId}/target-attributes`,
    targetAttribute,
  )
}

export async function patchTargetAttribute(
  templateId: number,
  targetAttributeId: number,
  updatedTargetAttribute: Partial<TargetAttribute>,
) {
  return caseConverterClient.patch<TargetAttribute>(
    `/api/templates/${templateId}/target-attributes/${targetAttributeId}`,
    updatedTargetAttribute,
  )
}

export async function deleteTargetAttribute(
  templateId: number,
  targetAttributeId: number,
) {
  return caseConverterClient.delete<DenormalizedTargetAttribute>(
    `/api/templates/${templateId}/target-attributes/${targetAttributeId}`,
  )
}

export async function getTemplate(templateId: number) {
  return caseConverterClient.get<TemplateWithSnapshots>(`/api/templates/${templateId}`)
}

export async function pushTemplateToEnvironments(
  templateId: number,
  environmentIds: number[],
) {
  return caseConverterClient.post<DenormalizedTemplate>(
    `/api/templates/${templateId}/push-to-environments`,
    { environmentIds },
  )
}

export async function getTemplateSkeletons() {
  return caseConverterClient.get<TemplateSkeleton[]>("api/template-skeletons")
}

interface GetTemplatesRequestParams {
  page?: number
  pageSize?: number
  beforeTime?: string
  searchTerm?: string
  filterModel?: {
    [attribute: string]: {
      filterType: string
      type?: string
      values: string[]
    }
  }
  sortModel?: {
    colId: string
    sort: string
  }
  /**
   * Filter templates by template-tag IDs. When multiple IDs are provided,
   * `tagMatch` controls whether templates must match all tags (AND) or
   * any tag (OR). Gated behind the `TemplateTags` feature flag.
   */
  templateTagIds?: number[]
  tagMatch?: TagMatchMode
}

interface GetTemplatesResponse {
  templates: TemplatesPageTemplate[]
  count: number
}

// This is currently used by the all templates page. We might rename this in the future to reflect the specific way we're using it.
export async function getTemplates(
  params: GetTemplatesRequestParams,
  signal?: AbortSignal,
) {
  return caseConverterClient.get<GetTemplatesResponse>("api/templates", {
    params,
    paramsSerializer: paramsSerializerV0,
    signal,
  })
}

export async function getTemplatesCount() {
  return caseConverterClient.get<{
    totalCount: number
    apiTemplateCount: number
    agentTemplateCount: number
    nonsampleTemplateCount: number
  }>("/api/templates-count")
}

export async function generateSampleFile(templateId: number) {
  return caseConverterClient.get<Blob>(`/api/templates/${templateId}/sample-file`, {
    responseType: "blob",
  })
}
export interface TemplateSkeletonWithMetadata extends TemplateSkeleton {
  pushedToProduction: boolean
  isApiGenerated: boolean
  isAgentGenerated: boolean
}

interface GetTemplateSkeletonsWithMetadataRequestParams {
  filterModel?: {
    [attribute: string]: {
      filterType: string
      type?: string
      values: string[]
    }
  }
}

export async function getTemplateSkeletonsWithMetadata(
  params: GetTemplateSkeletonsWithMetadataRequestParams,
) {
  return caseConverterClient.get<TemplateSkeletonWithMetadata[]>(
    "/api/template-skeletons-with-metadata",
    {
      params,
      paramsSerializer: paramsSerializerV0,
    },
  )
}

export async function getApiGeneratedTemplateSkeletons(): Promise<
  TemplateSkeletonWithMetadata[]
> {
  const { data: templateSkeletons } = await getTemplateSkeletonsWithMetadata({
    filterModel: {
      creationSource: {
        filterType: "text",
        type: "contains",
        values: ["EXTERNAL_API"],
      },
    },
  })
  return templateSkeletons
}

export async function getAgentGeneratedTemplateSkeletons(): Promise<
  TemplateSkeletonWithMetadata[]
> {
  const { data: templateSkeletons } = await getTemplateSkeletonsWithMetadata({
    filterModel: {
      creationSource: {
        filterType: "text",
        type: "contains",
        values: ["TEMPLATE_AGENT"],
      },
    },
  })
  return templateSkeletons
}

export async function getTemplateSkeletonsByIds(
  templateIds: number[],
): Promise<TemplateSkeletonWithMetadata[]> {
  const { data: templateSkeletons } = await getTemplateSkeletonsWithMetadata({
    filterModel: {
      id: {
        filterType: "id",
        values: templateIds.map(String),
      },
    },
  })
  return templateSkeletons
}

export async function getTemplateSkeletonsByKeys(
  templateKeys: string[],
): Promise<TemplateSkeletonWithMetadata[]> {
  const { data: templateSkeletons } = await getTemplateSkeletonsWithMetadata({
    filterModel: {
      template_key: {
        filterType: "key",
        values: templateKeys,
      },
    },
  })
  return templateSkeletons
}
